import cn from 'classnames';
import { type FC, useMemo } from 'react';

import { getTargetNameForTasks } from '@/components/Tasks/Show/Tasks.utils';
import Link from '@/components/UI/Link';
import { _notNil } from '@/littledash';
import type { TaskTarget } from '@/model/Task.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { web } from '@/support/route';
import { sortAscByProperty } from '@/utils/array.utils';
import { ModalActions, ModalContainer, ModalHeader, useModalAction } from '@/utils/modal';

import { OverviewGroupDetailsContent } from './OverviewGroupDetailsContent';
import styles from './OverviewGroupDetails.module.scss';

interface OverviewGroupDetailsProps {
  target: TaskTarget;
  groups: Array<TreatmentGroup>;
  studyId: string | null;
  taskTitle: string;
  subText?: string;
  emptyMessage?: string;
}

const getGroupKey = (group: TreatmentGroup): string => {
  return (group.api_id ?? group.temp_id) as string;
};

/**
 * Overview copy of the task target details modal. Study overview opens this for
 * treatment and sampling-plan group chips so that view can change independently
 * of task targeting.
 */
export const OverviewGroupDetails: FC<OverviewGroupDetailsProps> = ({
  target,
  groups,
  studyId,
  taskTitle,
  subText,
  emptyMessage,
}) => {
  const { closeModal } = useModalAction();

  const groupsById = useMemo(() => {
    return groups.reduce<Map<string, TreatmentGroup>>((acc, group) => {
      const key = getGroupKey(group);
      if (_notNil(key)) {
        acc.set(key, group);
      }
      return acc;
    }, new Map());
  }, [groups]);

  const targetGroups = useMemo(() => {
    if (target.type !== 'group') {
      return [];
    }

    return target.groups
      .reduce<Array<TreatmentGroup>>((acc, id) => {
        const group = groupsById.get(id);
        if (_notNil(group)) {
          acc.push(group);
        }
        return acc;
      }, [])
      .sort(sortAscByProperty('no'));
  }, [groupsById, target]);

  return (
    <ModalContainer size="small" testKey="overview-group-details">
      <ModalHeader
        title={taskTitle}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={subText ?? `This task applies to ${getTargetNameForTasks(target).toLowerCase()}`}
      />
      <div
        className={cn('pa3 overflow-y-auto', styles.content)}
        data-test-component="OverviewGroupDetails"
        data-test-element="content"
      >
        <OverviewGroupDetailsContent target={target} targetGroups={targetGroups} emptyMessage={emptyMessage} />
      </div>
      {_notNil(studyId) && (
        <ModalActions
          className="pa3 bt b--moon-gray"
          asideComponent={
            <Link
              className="link"
              to={web('studies.studyGroups', { id: studyId })}
              onClick={() => closeModal()}
              data-testid="overview-group-details-groups-link"
            >
              Go to treatment groups
            </Link>
          }
        />
      )}
    </ModalContainer>
  );
};
